/**
 * useHyperFetch is a custom hook that abstracts the logic of making api request to the backend.
 * at core, it uses axios to make the request.
 */

import { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import { dispatchCustomEvent } from "../utils/dispatchCustomEvent";
import { constants } from "../config/constants";
import { apiClient, interceptAxios } from "../api/api";
import { useAppStore } from "../store";
import { selectUser } from "../store/data/user";

type TMthodParam<TPayload> = {
  [P in keyof TPayload]: TPayload[P];
} & {
  appendPath?: string | number | (string | number)[];
  queryParams?: Record<string, any>;
};

export type TSesssionUnauthPayload = {
  err: any;
  onRefetch: <T = any>(newResponse: T) => void;
};

export function useHyperFetch<TPayload = any, TData = any>(route: string) {
  const user = useAppStore(selectUser);
  // interceptAxios();

  // handles post request
  const post = async (
    payload?: TMthodParam<TPayload>
  ): Promise<{
    response: AxiosResponse & { error: AxiosError };
    data: TData;
    error: Error | null;
  }> => {
    if (!payload) payload = {} as TMthodParam<TPayload>;
    let data;
    let error = null;
    let response = {} as AxiosResponse;
    try {
      response = await apiClient.post(route, payload);
      data = response.data;
    } catch (err) {
      error = err;
    } finally {
      return {
        response: { ...response, error: error as AxiosError },
        data: data as TData,
        error: error as Error | null,
      };
    }
  };

  // handles get request
  const get = async (
    payload?: TMthodParam<TPayload>
  ): Promise<{
    response: AxiosResponse & { error: AxiosError };
    data: TData;
    error: Error | null;
  }> => {
    if (!payload) payload = {} as TMthodParam<TPayload>;
    const { appendPath, queryParams, ...restPayload } = payload;

    let data, error;
    let response = {} as AxiosResponse;
    try {
      // apend path to the route
      if (typeof appendPath === "string" || typeof appendPath === "number") {
        route = `${route}/${String(appendPath)}`;
      }
      if (Array.isArray(appendPath)) {
        route = appendPath.reduce(
          (acc, curr) => `${String(acc)}/${String(curr)}`,
          route
        ) as string;
      }

      // adding query params
      if (queryParams) {
        const query = Object.entries(queryParams)
          .map(([key, value]) => `${key}=${value}`)
          .join("&");
        route = `${route}?${query}`;
      }

      response = await apiClient.get(route, restPayload);
      data = response.data;
    } catch (err) {
      error = err;
    } finally {
      return {
        response: { ...response, error: error as AxiosError },
        data: data as TData,
        error: error as Error | null,
      };
    }
  };

  return { post, get };
}
